import type { MouseEvent, ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { transitions } from '../../../common/transitions';
import { Tooltip } from '../../../components/Tooltip/Tooltip';
import { colors, css, darkThemeSelector, keyframes, styled } from '../../../stitches.config';
import { copyTextToClipboard } from '../../../utilities/clipboard';
import { useTimedToggle } from '../../../utilities/useTimedToggle';
import { UtilityControlDecoration } from '../../UtilityControl/UtilityControl';

type FieldAnchorCopyHandlers = {
  onCopy?: () => void;
  onCopyError?: (error: unknown) => void;
};

const FieldAnchorContext = createContext<
  ({ target: string | null } & FieldAnchorCopyHandlers) | null
>(null);

function decodeHash(hash: string) {
  const fragment = hash.replace(/^#/, '');
  if (!fragment) return null;
  try {
    return decodeURIComponent(fragment);
  } catch {
    return fragment;
  }
}

/**
 * Gives named fields within it a copyable link, and scrolls to and highlights the field whose
 * name matches `hash`.
 */
export function FieldAnchorProvider({
  hash,
  onCopy,
  onCopyError,
  children,
}: { hash: string; children: ReactNode } & FieldAnchorCopyHandlers) {
  const value = useMemo(
    () => ({ target: decodeHash(hash), onCopy, onCopyError }),
    [hash, onCopy, onCopyError],
  );
  return <FieldAnchorContext.Provider value={value}>{children}</FieldAnchorContext.Provider>;
}

const highlight = keyframes({
  '0%, 40%': { opacity: 1 },
  '100%': { opacity: 0 },
});

export const FieldAnchorTarget = css({
  '&[data-field-anchor-target]': {
    position: 'relative',
    isolation: 'isolate',

    '&::before': {
      content: '',
      position: 'absolute',
      inset: '$4',
      zIndex: -1,
      borderRadius: '$6',
      background: colors.bgAttentionLight,
      strokeAll: colors.strokeAttentionLight,
      opacity: 0,
      pointerEvents: 'none',
      animation: `${highlight} 2.5s ease-out`,

      [darkThemeSelector]: {
        background: colors.bgAttentionDark,
        strokeAll: colors.strokeAttentionDark,
      },
    },
  },
});

const FieldAnchorLinkContainer = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  opacity: 0,
  transition: transitions.control,

  '[data-field-anchor]:hover &': {
    opacity: 1,
  },
});
FieldAnchorLinkContainer.displayName = 'FieldAnchorLinkContainer';

const FieldAnchorLinkAnchor = styled('a', {
  display: 'inline-flex',
  textDecoration: 'none',
});
FieldAnchorLinkAnchor.displayName = 'FieldAnchorLinkAnchor';

function FieldAnchorLink({ anchor }: { anchor: string }) {
  const context = useContext(FieldAnchorContext);
  const { value: copied, show } = useTimedToggle(2000);
  const label = copied ? 'Copied link' : 'Copy link';

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    copyTextToClipboard(event.currentTarget.href)
      .then(() => {
        show();
        context?.onCopy?.();
      })
      .catch((error: unknown) => context?.onCopyError?.(error));
  };

  return (
    <FieldAnchorLinkContainer>
      <Tooltip contents={label} toggle={false}>
        <FieldAnchorLinkAnchor
          href={`#${encodeURIComponent(anchor)}`}
          aria-label={label}
          onClick={handleClick}
          // Taking focus would blur the field's input, which marks it touched and shows its errors.
          onMouseDown={(event) => event.preventDefault()}
          tabIndex={-1}
        >
          <UtilityControlDecoration icon={copied ? 'checkmark' : 'link'} />
        </FieldAnchorLinkAnchor>
      </Tooltip>
    </FieldAnchorLinkContainer>
  );
}

export function useFieldAnchor(name: string | undefined) {
  const context = useContext(FieldAnchorContext);
  const anchor = context && name ? name : null;
  const isTarget = anchor !== null && context?.target === anchor;

  const ref = useCallback(
    (node: HTMLElement | null) => {
      if (node && isTarget) node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    },
    [isTarget],
  );

  return {
    containerProps: anchor
      ? {
          ref,
          'data-field-anchor': '',
          'data-field-anchor-target': isTarget ? '' : undefined,
        }
      : {},
    link: anchor ? <FieldAnchorLink anchor={anchor} /> : null,
  };
}
